import { type HTMLAttributes, type ReactNode } from 'react';
import { VerifiedStatusIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type BadgeTone =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  /** F09 v2 — info-toned pill that auto-injects the verified glyph. */
  | 'verified';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: BadgeTone;
  readonly size?: 'sm' | 'md';
  /**
   * Leading icon. For `tone="verified"` the verified glyph is injected when this
   * is omitted; pass `null` to render the verified pill without a glyph.
   */
  readonly icon?: ReactNode;
}

const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-ink-2 border border-hairline-2',
  accent: 'bg-accent-soft text-ink',
  success: 'bg-success/15 text-ink',
  warning: 'bg-warning/15 text-ink',
  danger: 'bg-danger/15 text-ink',
  info: 'bg-info/15 text-ink',
  verified: 'bg-info/15 text-ink',
};

const ICON_TONE: Record<BadgeTone, string> = {
  neutral: 'text-ink-2',
  accent: 'text-accent-text',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
  verified: 'text-info',
};

const SIZE = {
  sm: 'h-5 px-1.5 text-[10px]',
  md: 'h-6 px-2 text-[11px]',
};

const VERIFIED_GLYPH = { sm: 10, md: 12 } as const;

export function Badge({
  tone = 'neutral',
  size = 'md',
  icon,
  className,
  children,
  ...rest
}: BadgeProps) {
  const leading =
    icon !== undefined ? (
      icon
    ) : tone === 'verified' ? (
      <VerifiedStatusIcon size={VERIFIED_GLYPH[size]} color="currentColor" />
    ) : null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-semibold uppercase tracking-wider whitespace-nowrap',
        TONE[tone],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {leading ? (
        <span className={cn('inline-flex shrink-0', ICON_TONE[tone])}>{leading}</span>
      ) : null}
      {children}
    </span>
  );
}
